import * as Yup from 'yup';

import { BRANCH_TYPE, EIV_CHAR_LENGTH, TENANT_ID, VALIDATION_CONSTANTS } from 'global/constants';

import {
  isRegisteringBranchGuest,
  isRegisteringInstallerGuestOnly,
} from 'utils/InstallerUtils';
import { YDate, YPhoneNumber, YZipRequired } from 'utils/YupUtils';
import { getTenantId } from 'utils/tenantUtil';

export const YNEFIStepFormPageSchema = (branchSelectionValues) =>
  Yup.object().shape({
    companyName1: Yup.string()
      .required(VALIDATION_CONSTANTS.MANDATORY)
      .min(2, VALIDATION_CONSTANTS.TOO_SHORT)
      .max(EIV_CHAR_LENGTH.FIRMENNAME1, VALIDATION_CONSTANTS.TOO_LONG),
    companyName2: Yup.lazy((value) => {
      if (value !== undefined && value !== '') {
        return Yup.string()
          .min(2, VALIDATION_CONSTANTS.TOO_SHORT)
          .max(EIV_CHAR_LENGTH.FIRMENNAME2, VALIDATION_CONSTANTS.TOO_LONG);
      }
      return Yup.string().nullable().optional();
    }),
    companyName3: Yup.lazy((value) => {
      if (value !== undefined && value !== '') {
        return Yup.string()
          .min(2, VALIDATION_CONSTANTS.TOO_SHORT)
          .max(EIV_CHAR_LENGTH.FIRMENNAME2, VALIDATION_CONSTANTS.TOO_LONG);
      }
      return Yup.string().nullable().optional();
    }),
    companyCountry: Yup.string().required(VALIDATION_CONSTANTS.MANDATORY),
    b_anrede_id: Yup.string().required(VALIDATION_CONSTANTS.MANDATORY),
    taetigkeitsschwerpunkt: Yup.string().required(VALIDATION_CONSTANTS.MANDATORY),
    companyOwner: Yup.lazy(() => {
      if (TENANT_ID.BAG === getTenantId()) {
        return Yup.string()
          .required(VALIDATION_CONSTANTS.MANDATORY)
          .min(2, VALIDATION_CONSTANTS.TOO_SHORT)
          .max(EIV_CHAR_LENGTH.FIRMENNAME3, VALIDATION_CONSTANTS.TOO_LONG);
      }
      return Yup.string().nullable().optional();
    }),
    companyStreet: Yup.string()
      .required(VALIDATION_CONSTANTS.MANDATORY)
      .min(2, VALIDATION_CONSTANTS.TOO_SHORT)
      .max(EIV_CHAR_LENGTH.BUERO_STRASSE, VALIDATION_CONSTANTS.TOO_LONG),
    companyHouseNumber: Yup.string()
      .required(VALIDATION_CONSTANTS.MANDATORY)
      .min(1, VALIDATION_CONSTANTS.TOO_SHORT)
      .max(EIV_CHAR_LENGTH.BUERO_HAUSNUMMER, VALIDATION_CONSTANTS.TOO_LONG),
    companyPostal: Yup.lazy((value, context) => {
      if (context?.parent?.companyCountry === 'DE') {
        return Yup.string()
          .required(VALIDATION_CONSTANTS.MANDATORY)
          .min(5, VALIDATION_CONSTANTS.TOO_SHORT)
          .max(EIV_CHAR_LENGTH.BUERO_PLZ, VALIDATION_CONSTANTS.TOO_LONG);
      }
      return Yup.string()
        .required(VALIDATION_CONSTANTS.MANDATORY)
        .min(4, VALIDATION_CONSTANTS.TOO_SHORT)
        .max(EIV_CHAR_LENGTH.BUERO_PLZ, VALIDATION_CONSTANTS.TOO_LONG);
    }),
    companyCity: Yup.string()
      .required(VALIDATION_CONSTANTS.MANDATORY)
      .min(2, VALIDATION_CONSTANTS.TOO_SHORT)
      .max(EIV_CHAR_LENGTH.BUERO_ORT, VALIDATION_CONSTANTS.TOO_LONG),
    companyRegion: Yup.lazy((value) => {
      if (value !== undefined && value !== '') {
        return Yup.string()
          .min(2, VALIDATION_CONSTANTS.TOO_SHORT)
          .max(EIV_CHAR_LENGTH.BUERO_ORTSTEIL, VALIDATION_CONSTANTS.TOO_LONG);
      }
      return Yup.string().nullable().optional();
    }),
    mobile: Yup.lazy((value) => {
      if (value !== undefined && value !== '') {
        return YPhoneNumber;
      }
      return Yup.string()
        .nullable()
        .optional()
        .test('mobile-or-phone-required', VALIDATION_CONSTANTS.MANDATORY, function (val) {
          return !!(val || this.parent.phone);
        });
    }),
    phone: Yup.lazy((value) => {
      if (value !== undefined && value !== '') {
        return YPhoneNumber;
      }
      return Yup.string()
        .nullable()
        .optional()
        .test('mobile-or-phone-required', VALIDATION_CONSTANTS.MANDATORY, function (val) {
          return !!(val || this.parent.mobile);
        });
    }),
    gridProviderEnergy: Yup.lazy(() => {
      if (isRegisteringBranchGuest(BRANCH_TYPE.ENERGY, branchSelectionValues)) {
        return Yup.number().required(VALIDATION_CONSTANTS.MANDATORY);
      }
      return Yup.string().nullable().optional();
    }),
    foreignCompanyIdEnergy: Yup.lazy(() => {
      if (isRegisteringBranchGuest(BRANCH_TYPE.ENERGY, branchSelectionValues)) {
        return Yup.string()
          .required(VALIDATION_CONSTANTS.MANDATORY)
          .min(2, VALIDATION_CONSTANTS.TOO_SHORT)
          .max(EIV_CHAR_LENGTH.AUSWEISNR_FREMD, VALIDATION_CONSTANTS.TOO_LONG);
      }
      return Yup.string().nullable().optional();
    }),
    gridProviderGas: Yup.lazy(() => {
      if (isRegisteringBranchGuest(BRANCH_TYPE.GAS, branchSelectionValues)) {
        return Yup.number().required(VALIDATION_CONSTANTS.MANDATORY);
      }
      return Yup.string().nullable().optional();
    }),
    foreignCompanyIdGas: Yup.lazy(() => {
      if (isRegisteringBranchGuest(BRANCH_TYPE.GAS, branchSelectionValues)) {
        return Yup.string()
          .required(VALIDATION_CONSTANTS.MANDATORY)
          .min(2, VALIDATION_CONSTANTS.TOO_SHORT)
          .max(EIV_CHAR_LENGTH.AUSWEISNR_FREMD, VALIDATION_CONSTANTS.TOO_LONG);
      }
      return Yup.string().nullable().optional();
    }),
    experts: Yup.array().min(1, VALIDATION_CONSTANTS.MANDATORY),
    craftsChamberArea: Yup.string().nullable().optional(),
    craftsChamberNumber: Yup.string().nullable().optional(),
    craftsChamberDate: Yup.string().nullable().optional(),
    businessRegistrationLocation: Yup.string().nullable().optional(),
    businessRegistrationDate: Yup.string().nullable().optional(),
    commercialRegisterNumber: Yup.lazy((value) => {
      if (
        TENANT_ID.BAG === getTenantId() &&
        !isRegisteringInstallerGuestOnly(branchSelectionValues) &&
        value !== undefined &&
        value !== ''
      ) {
        return Yup.string()
          .min(2, VALIDATION_CONSTANTS.TOO_SHORT)
          .max(EIV_CHAR_LENGTH.BUERO_STRASSE, VALIDATION_CONSTANTS.TOO_LONG);
      }
      return Yup.string().nullable().optional();
    }),
    commercialRegisterLocation: Yup.lazy((value) => {
      if (
        TENANT_ID.BAG === getTenantId() &&
        !isRegisteringInstallerGuestOnly(branchSelectionValues) &&
        value !== undefined &&
        value !== ''
      ) {
        return Yup.string()
          .min(2, VALIDATION_CONSTANTS.TOO_SHORT)
          .max(EIV_CHAR_LENGTH.BUERO_STRASSE, VALIDATION_CONSTANTS.TOO_LONG);
      }
      return Yup.string().nullable().optional();
    }),
    commercialRegisterDate: Yup.lazy((value) => {
      if (
        TENANT_ID.BAG === getTenantId() &&
        !isRegisteringInstallerGuestOnly(branchSelectionValues) &&
        value !== undefined &&
        value !== ''
      ) {
        return YDate;
      }
      return Yup.string().nullable().optional();
    }),
    avaGuestPostal: Yup.lazy(() => {
      if (
        TENANT_ID.AVA === getTenantId() &&
        isRegisteringInstallerGuestOnly(branchSelectionValues)
      ) {
        return YZipRequired;
      }
      return Yup.string().nullable().optional();
    }),
  });
